import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../environments/environment';
import { IOtpRequest } from '../interfaces/otp-request.interface';
import { ILoginRequest } from '../interfaces/user/login-request.interface';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _errorHandler = inject(ErrorHandlerService);
  private _toastr = inject(ToastrService);
  private _router = inject(Router);

  private env = environment;
  currentUserToken = signal<string | null>(null);
  refreshUserToken = signal<string | null>(null);

  // Computed signal to check if user is logged in
  isAuthenticated = computed(() => !!this.currentUserToken());

  // Computed signal to get email from token
  userEmail = computed(() => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      const decoded: any = jwtDecode(token);
      return decoded.email || null;
    } catch (error) {
      return null;
    }
  });

  private async _fetchLongLivedToken(refreshToken: string): Promise<void> {
    const payload = {
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    };

    const response = await fetch(`${this.env.API_URL}/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok) {
      this._errorHandler.handleError(data.message);
      return;
    }

    this._handleAuthSuccess(data);
  }

  private _handleAuthSuccess(data: any): void {
    if (data.token && data.refresh_token && data.token_expiration) {
      this.currentUserToken.set(data.token);
      this.refreshUserToken.set(data.refresh_token);

      localStorage.setItem('token', data.token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('token_expiration', data.token_expiration);
    }
  }

  async login(user: ILoginRequest): Promise<void> {
    try {
      const formData = new URLSearchParams();
      formData.append('email', user.email);
      formData.append('password', user.password);

      const response = await fetch(`${this.env.API_URL}/auth/token/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      const data = await response.json();
      if (!response.ok) return this._errorHandler.handleError(data.message);

      console.log('login response', data);

      if (data.otp_temp_code) {
        this._router.navigate(['/authentication'], {
          queryParams: { code: data.otp_temp_code },
        });
      } else {
        await this._fetchLongLivedToken(data.refresh_token);
        this._router.navigate(['/select-organization']);
      }
    } catch (error: any) {
      this._errorHandler.handleError(error.message);
    }
  }

  async verifyOtp(otpData: IOtpRequest): Promise<void> {
    try {
      const formData = new URLSearchParams();
      formData.append('otp_temp_code', otpData.otp_temp_code);
      formData.append('otp', otpData.otp);

      const response = await fetch(`${this.env.API_URL}/auth/token/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      const data = await response.json();
      if (!response.ok) return this._errorHandler.handleError(data.message);

      await this._fetchLongLivedToken(data.refresh_token);
      this._router.navigate(['/select-organization']);
    } catch (error: any) {
      this._errorHandler.handleError(error.message);
    }
  }

  async resetPassword(email: string): Promise<void> {
    try {
      const response = await fetch(
        `${
          this.env.API_URL
        }/auth/password/send_reset_email?email=${encodeURIComponent(email)}`,
        { method: 'POST' },
      );

      if (!response.ok) {
        const data = await response.json();
        this._errorHandler.handleError(data.message);
      }

      this._toastr.success('Check your email for the password reset link.');
      this._router.navigate(['/']);
    } catch (error: any) {
      this._errorHandler.handleError(error.message);
    }
  }

  async refreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.env.API_URL}/auth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        this.logout();
        return false;
      }

      this._handleAuthSuccess(data);
      return true;
    } catch (error: any) {
      this.logout();
      return false;
    }
  }

  async getSecuredToken(subdomain: string): Promise<string | null> {
    try {
      const url = `${
        this.env.API_URL
      }/auth/token/secured?subdomain=${encodeURIComponent(subdomain)}`;

      const response = await fetchWithAuth(url, {
        method: 'GET',
      });

      const data = await response.json();
      if (!response.ok) {
        this._errorHandler.handleError(data.message);
        return null;
      }

      console.log('getSecuredToken response', data);

      return data.refresh_token;
    } catch (error: any) {
      this._errorHandler.handleError(error.message);
      return null;
    }
  }

  logout(): void {
    this.currentUserToken.set(null);
    this.refreshUserToken.set(null);

    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiration');

    this._router.navigate(['/']);
  }
}
